import * as _ from 'lodash';
import * as Types from '../types';
import * as Vega3 from '../../../util/vega3';
import * as Graph from './graph';
import * as HL from './hl';
import * as MediaTable from '../mediaTable';
import * as Path from '../../../util/path';
import * as File from '../files';

const gqlBasicField = (name: string) => {
  const field: Vega3.QueryField = {
    name,
    fields: [],
  };
  return [field];
};

const gqlArgs = (args: {[key: string]: any}): Vega3.QueryArg[] | undefined => {
  if (Object.keys(args).length === 0) {
    return undefined;
  }
  return _.map(args, (v, key) => ({name: key, value: v}));
};

const gqlFieldWithArgs = (name: string, args?: Vega3.QueryArg[]) => {
  const field: Vega3.QueryField = {
    name,
    args,
    fields: [],
  };
  return [field];
};

const gqlObjectField = (
  forwardGraph: Graph.ForwardGraph,
  forwardOp: Graph.ForwardOp,
  name: string,
  args?: Vega3.QueryArg[],
  opts?: {
    extraFields?: Vega3.QueryField[];
    alias?: string;
  }
) => {
  const extraFields = opts?.extraFields ?? [];
  const alias = opts?.alias;
  const field: Vega3.QueryField = {
    name,
    args,
    fields: [
      {name: 'id', fields: []},
      ...gqlObjectSubfields(forwardGraph, forwardOp),
      ...(extraFields ?? []),
    ],
    alias,
  };
  return field;
};

const gqlObjectSubfields = (
  forwardGraph: Graph.ForwardGraph,
  forwardOp: Graph.ForwardOp
): Vega3.QueryField[] => {
  return Array.from(forwardOp.outputNode.inputTo).flatMap(inputToForwardOp =>
    toGqlField(forwardGraph, inputToForwardOp)
  );
};

const toGqlField = (
  forwardGraph: Graph.ForwardGraph,
  forwardOp: Graph.ForwardOp
): Vega3.QueryField[] => {
  const opInputs = Graph.forwardOpInputs(forwardGraph, forwardOp);

  if (forwardOp.op.name === 'root-project') {
    return [
      gqlObjectField(
        forwardGraph,
        forwardOp,
        'project',
        gqlArgs({
          entityName: opInputs.entityName,
          name: opInputs.projectName,
        })
      ),
    ];
  } else if (forwardOp.op.name === 'root-artifact') {
    return [
      {
        name: 'project',
        args: gqlArgs({
          entityName: opInputs.entityName,
          name: opInputs.projectName,
        }),
        fields: [
          {name: 'id', fields: []},
          {
            name: 'artifactType',
            args: gqlArgs({name: opInputs.artifactTypeName}),
            fields: [
              {name: 'id', fields: []},
              gqlObjectField(
                forwardGraph,
                forwardOp,
                'artifact',
                gqlArgs({name: opInputs.artifactId})
              ),
            ],
          },
        ],
      },
    ];
  } else if (forwardOp.op.name === 'root-org') {
    return [
      gqlObjectField(
        forwardGraph,
        forwardOp,
        'org',
        gqlArgs({
          name: opInputs.orgName,
        })
      ),
    ];
  } else if (forwardOp.op.name === 'project-id') {
    return gqlBasicField('id');
  } else if (forwardOp.op.name === 'project-createdAt') {
    return gqlBasicField('createdAt');
  } else if (forwardOp.op.name === 'project-name') {
    return gqlBasicField('name');
  } else if (forwardOp.op.name === 'project-stars') {
    return gqlBasicField('stars');
  } else if (forwardOp.op.name === 'project-runs') {
    // "lift" runs-index fields to here.
    const runsIndexForwardOp = Graph.findChildOp(forwardOp, 'index');
    // Add an aliased field for runs-filter. We need to fetch both project-runs
    // and runs-filter nodes separately currently. This is not general at all, and
    // will break.
    // Actual solution is detecting child nodes that need to be fetched (we don't
    // know what we're fetching here... might be better to send that information
    // in actually..... what's being listened to?, rather than trying to detect).
    // Anyway, for any listening nodes, we need to roll op child filter/group/sort
    // ops to here, but give them all separate aliases (any of the ones listened to
    // to ensure we get unique results for each).
    // Some could still likely be merged after finding them?
    const filteredForwardOp = Graph.findChildOp(forwardOp, 'runs-filter');
    return [
      {
        name: 'runs',
        // TODO: hardcoding for perf during dev
        args: gqlArgs({first: 10}),
        fields: [
          {
            name: 'edges',
            fields: [
              gqlObjectField(forwardGraph, forwardOp, 'node', gqlArgs({}), {
                extraFields:
                  runsIndexForwardOp != null
                    ? gqlObjectSubfields(forwardGraph, runsIndexForwardOp)
                    : [],
              }),
            ],
          },
        ],
      },
      ...(filteredForwardOp != null
        ? [
            {
              name: 'runs',
              // TODO: hardcoding for perf during dev
              args: gqlArgs({
                first: 10,
                filter: Graph.forwardOpInputs(forwardGraph, filteredForwardOp)
                  .filter,
              }),
              fields: [
                {
                  name: 'edges',
                  fields: [
                    gqlObjectField(
                      forwardGraph,
                      forwardOp,
                      'node',
                      gqlArgs({})
                    ),
                  ],
                },
              ],
              alias: 'runs-filter',
            },
          ]
        : []),
    ];
  } else if (forwardOp.op.name === 'org-members') {
    // "lift" users-index fields to here.
    const usersIndexForwardOp = Graph.findChildOp(forwardOp, 'index');
    // Note, as compared to runs, we don't handle server-side filtering
    return [
      gqlObjectField(forwardGraph, forwardOp, 'members', gqlArgs({}), {
        extraFields:
          usersIndexForwardOp != null
            ? gqlObjectSubfields(forwardGraph, usersIndexForwardOp)
            : [],
      }),
    ];
  } else if (forwardOp.op.name === 'user-runs') {
    // Note, as compared to runs, we don't handle server-side filtering
    return [gqlObjectField(forwardGraph, forwardOp, 'runs', gqlArgs({}))];
  } else if (forwardOp.op.name === 'user-username') {
    // Note, as compared to runs, we don't handle server-side filtering
    return gqlBasicField('username');
  } else if (forwardOp.op.name === 'artifact-size') {
    return gqlBasicField('size');
  } else if (forwardOp.op.name === 'runs-stars') {
    return gqlBasicField('stars');
  } else if (forwardOp.op.name === 'run-name') {
    return gqlBasicField('displayName');
  } else if (forwardOp.op.name === 'run-stars') {
    return gqlBasicField('stars');
  } else if (forwardOp.op.name === 'run-summary') {
    return gqlFieldWithArgs('summaryMetrics', gqlArgs({keys: [opInputs.key]}));
  }
  return [];
};

// This must be the inverse of toGqlField (should be "toQuery")
const applyResult = (
  forwardGraph: Graph.ForwardGraph,
  forwardOp: Graph.ForwardOp,
  result: any,
  // TODO: get rid of this, make function less generic
  parentType?: 'run' | 'user'
) => {
  const op = forwardOp.op;
  forwardOp.outputNode.hasResult = true;
  forwardOp.outputNode.result = result;
  if (op.name === 'root-project') {
    for (const inputToForwardOp of forwardOp.outputNode.inputTo) {
      if (inputToForwardOp.op.name === 'project-stars') {
        applyResult(forwardGraph, inputToForwardOp, result.stars);
      } else if (inputToForwardOp.op.name === 'project-id') {
        applyResult(forwardGraph, inputToForwardOp, result.id);
      } else if (inputToForwardOp.op.name === 'project-createdAt') {
        // Note, this is where we do the date conversion
        applyResult(
          forwardGraph,
          inputToForwardOp,
          new Date(result.createdAt + 'Z')
        );
      } else if (inputToForwardOp.op.name === 'project-name') {
        applyResult(forwardGraph, inputToForwardOp, result.name);
      } else if (inputToForwardOp.op.name === 'project-runs') {
        applyResult(
          forwardGraph,
          inputToForwardOp,
          result.runs.edges.map((e: any) => e.node)
        );
        const filteredForwardOp = Array.from(
          inputToForwardOp.outputNode.inputTo
        ).find(fo => fo.op.name === 'runs-filter');
        if (filteredForwardOp != null) {
          applyResult(
            forwardGraph,
            filteredForwardOp,
            result['runs-filter'].edges.map((e: any) => e.node)
          );
        }
      }
    }
  } else if (op.name === 'root-artifact') {
    // Whoops make sure we apply deep result for internal nodes!
    forwardOp.outputNode.hasResult = true;
    forwardOp.outputNode.result = result.artifactType.artifact;
    for (const inputToForwardOp of forwardOp.outputNode.inputTo) {
      if (inputToForwardOp.op.name === 'artifact-size') {
        applyResult(
          forwardGraph,
          inputToForwardOp,
          result.artifactType.artifact.size
        );
      }
    }
  } else if (op.name === 'root-org') {
    for (const inputToForwardOp of forwardOp.outputNode.inputTo) {
      if (inputToForwardOp.op.name === 'org-members') {
        applyResult(forwardGraph, inputToForwardOp, result.members);
      }
    }
  } else if (
    op.name === 'org-members' ||
    (op.name === 'index' && parentType === 'user')
  ) {
    for (const inputToForwardOp of forwardOp.outputNode.inputTo) {
      if (inputToForwardOp.op.name === 'index') {
        const usersIndexInputs = Graph.forwardOpInputs(
          forwardGraph,
          inputToForwardOp
        );
        applyResult(
          forwardGraph,
          inputToForwardOp,
          result[usersIndexInputs.index],
          'user'
        );
      } else if (inputToForwardOp.op.name === 'user-runs') {
        applyResult(forwardGraph, inputToForwardOp, result.runs);
      } else if (inputToForwardOp.op.name === 'user-id') {
        applyResult(forwardGraph, inputToForwardOp, result.id);
      } else if (inputToForwardOp.op.name === 'user-username') {
        applyResult(forwardGraph, inputToForwardOp, result.username);
      }
    }
  } else if (
    op.name === 'user-runs' ||
    op.name === 'project-runs' ||
    op.name === 'runs-filter' ||
    (op.name === 'index' && parentType === 'run')
  ) {
    for (const inputToForwardOp of forwardOp.outputNode.inputTo) {
      if (inputToForwardOp.op.name === 'runs-stars') {
        applyResult(
          forwardGraph,
          inputToForwardOp,
          result.map((r: any) => r.stars)
        );
      } else if (inputToForwardOp.op.name === 'run-stars') {
        applyResult(forwardGraph, inputToForwardOp, result.stars);
      } else if (inputToForwardOp.op.name === 'run-name') {
        applyResult(forwardGraph, inputToForwardOp, result.displayName);
      } else if (inputToForwardOp.op.name === 'run-summary') {
        const runSummaryInputs = Graph.forwardOpInputs(
          forwardGraph,
          inputToForwardOp
        );
        const summary = JSON.parse(result.summaryMetrics);
        applyResult(
          forwardGraph,
          inputToForwardOp,
          summary[runSummaryInputs.key]
        );
      } else if (inputToForwardOp.op.name === 'index') {
        const runsIndexInputs = Graph.forwardOpInputs(
          forwardGraph,
          inputToForwardOp
        );
        applyResult(
          forwardGraph,
          inputToForwardOp,
          result[runsIndexInputs.index],
          'run'
        );
      }
    }
  }
};

// xx
export const opRootProject = Graph.makeOp(
  'root-project',
  {entityName: 'string', projectName: 'string'},
  'project',
  async (inputs, forwardGraph, forwardOp, context) => {
    // const {entityName, projectName} = inputs;
    const query = {
      queryFields: toGqlField(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query);
    // console.log(
    //   'QUERY opRootProject query, result',
    //   JSON.stringify(query, undefined, 2),
    //   JSON.stringify(result, undefined, 2)
    // );
    applyResult(forwardGraph, forwardOp, result.project);
  }
);

export const opRootArtifact = Graph.makeOp(
  'root-artifact',
  {
    entityName: 'string',
    projectName: 'string',
    artifactTypeName: 'string',
    artifactId: 'string',
  },
  'artifact',
  async (inputs, forwardGraph, forwardOp, context) => {
    // const {entityName, projectName} = inputs;
    const query = {
      queryFields: toGqlField(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query);
    // console.log(
    //   'QUERY opRootArtifact query, result',
    //   JSON.stringify(query, undefined, 2),
    //   JSON.stringify(result, undefined, 2)
    // );
    applyResult(forwardGraph, forwardOp, result.project);
  }
);

export const opRootOrg = Graph.makeOp(
  'root-org',
  {orgName: 'string'},
  'org',
  async (inputs, forwardGraph, forwardOp, context) => {
    const query2 = {
      queryFields: toGqlField(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query2);
    // console.log(
    //   'ORG QUERY RES',
    //   JSON.stringify(query2, undefined, 2),
    //   JSON.stringify(result, undefined, 2)
    // );
    applyResult(forwardGraph, forwardOp, result.org);
  }
);

export const opOrgMembers = Graph.makeOp(
  'org-members',
  {org: 'org'},
  {type: 'array', objectType: 'user'},
  inputs => {
    throw new Error('opOrgMembers not implemented');
  }
);

export const opUserId = Graph.makeOp('user-id', {user: 'user'}, 'string');

export const opUserUsername = Graph.makeOp(
  'user-username',
  {user: 'user'},
  'string'
);

export const opUserRuns = Graph.makeOp(
  'user-runs',
  {user: 'user'},
  {type: 'array', objectType: 'run'}
);

// xx
// TODO: Do we actually want to expose id fields like this to users?
export const opProjectId = Graph.makeOp(
  'project-id',
  {project: 'project'},
  'string'
);

// xx
export const opProjectCreatedAt = Graph.makeOp(
  'project-createdAt',
  {project: 'project'},
  'date'
);

// xx
export const opProjectName = Graph.makeOp(
  'project-name',
  {project: 'project'},
  'string'
);

// TODO: remove
export const opProjectStars = Graph.makeOp(
  'project-stars',
  {project: 'project'},
  'number'
);

export const opProjectArtifacts = Graph.makeOp(
  'project-artifacts',
  {project: 'project'},
  {type: 'array', objectType: 'artifact'},
  (inputs, forwardGraph, forwardOp, context) => {
    const {project} = inputs;
    const query: Vega3.Query = {
      queryFields: [
        {
          name: 'artifacts',
          args: [{name: 'projectId', value: project.id}],
          fields: [{name: 'id', fields: []}],
        },
      ],
    };
    return context.backend.execute(query);
  }
);

export const opArtifactsStars = Graph.makeOp(
  'artifacts-stars',
  {artifacts: {type: 'array', objectType: 'artifact'}},
  {type: 'array', objectType: 'number'},
  inputs => {
    const {artifacts} = inputs;
    return _.map(artifacts, 'stars');
  }
);

export const opArtifactsCreatedBy = Graph.makeOp(
  'artifacts-createdBy',
  {artifacts: {type: 'array', objectType: 'artifact'}},
  {type: 'array', objectType: 'run'},
  (inputs, forwardGraph, forwardOp, context) => {
    const {artifacts} = inputs;
    const query: Vega3.Query = {
      queryFields: [
        {
          name: 'artifacts-createdBy',
          args: [{name: 'artifactIds', value: artifacts.map((a: any) => a.id)}],
          fields: [{name: 'id', fields: []}],
        },
      ],
    };
    return context.backend.execute(query);
  }
);

export const opArtifactCreatedBy = Graph.makeOp(
  'artifact-createdBy',
  {artifact: 'artifact'},
  'run',
  async (inputs, forwardGraph, forwardOp, context) => {
    const {artifact} = inputs;
    const query: Vega3.Query = {
      queryFields: [
        {
          name: 'artifacts-createdBy',
          args: [{name: 'artifactIds', value: [artifact.id]}],
          fields: [{name: 'id', fields: []}],
        },
      ],
    };
    const res: any[] = await context.backend.execute(query);
    return res[0];
  }
);

export const opArtifactSize = Graph.makeOp(
  'artifact-size',
  {artifact: 'artifact'},
  'number'
);

export const opArtifactFile = Graph.makeOp(
  'artifact-file',
  {artifact: 'artifact', path: 'string'},
  {type: 'file'},
  async (inputs, forwardGraph, forwardOp, context) => {
    // NOTE: We're passing inputs back directly, which is artifact path
    // looks like {artifact: {id: <artifact_id>}, path: <string>}
    // TODO: not final, file doesn't need to be artifact dependent
    return inputs;
  },
  async (op, context) => {
    const outputNode: Graph.OutputNode = {
      nodeType: 'output',
      fromOp: op,
      type: null,
    };
    const fileTypeNode = opFileType({file: outputNode as any});
    const fileType = (
      await Graph.executeForwardFresh(context, [fileTypeNode])
    )[0];
    return {nodeType: 'output', fromOp: op, type: fileType};
  }
);

export const opFileType = Graph.makeOp(
  'file-type',
  {file: {type: 'file'}},
  {type: 'file'},
  async (inputs, forwardGraph, forwardOp, context) => {
    const {file} = inputs;
    const {artifact, path} = file;
    const result = await context.backend.getFileMetadata(artifact.id, path);
    if (result == null) {
      return null;
    } else if (result.type === 'dir') {
      return {type: 'dir' as const};
    } else {
      const filePath = path;
      const fileType: Types.File = {
        type: 'file' as const,
        extension: Path.extension(filePath),
      };
      if (filePath.endsWith('.json')) {
        const components = filePath.split('.');
        if (components.length >= 3) {
          const potentialWbObjectType = components[components.length - 2];
          if (Types.isMediaType(potentialWbObjectType)) {
            fileType.wbObjectType = potentialWbObjectType;
          }
        }
      }
      return fileType;
    }
  }
);

export const opFileDir = Graph.makeOp(
  'file-dir',
  {file: {type: 'dir'}},
  {type: 'table', columnTypes: {}},
  async (inputs, forwardGraph, forwardOp, context) => {
    const {file} = inputs;
    const {artifact, path} = file;
    const result = await context.backend.getFileMetadata(artifact.id, path);
    if (result == null) {
      return undefined;
      // throw new Error('didnt load table contents (opArtifactsTable)');
    }
    if (result.type !== 'dir') {
      throw new Error('expected dir');
    }
    return result;
  }
);

export const opFileSize = Graph.makeOp(
  'file-size',
  {file: {type: 'file'}},
  'number',
  async (inputs, forwardGraph, forwardOp, context) => {
    const {file} = inputs;
    const {artifact, path} = file;
    const result = await context.backend.getFileMetadata(artifact.id, path);
    if (result == null) {
      return undefined;
      // throw new Error('didnt load table contents (opArtifactsTable)');
    }
    if (result.type === 'dir') {
      throw new Error('expected file');
    }
    return result.size;
  }
);

const memoJSONParse = _.memoize(JSON.parse);

export const opFileTable = Graph.makeOp(
  'file-table',
  {file: {type: 'file', extension: 'json', wbObjectType: 'table'}},
  {type: 'table', columnTypes: {}},
  async (inputs, forwardGraph, forwardOp, context) => {
    const {file} = inputs;
    const {artifact, path} = file;
    const result = await context.backend.getFileContents(artifact.id, path);
    const contents = result.contents;
    if (contents == null) {
      console.log('opFileTable invalid table', inputs);
      throw new Error('invalid table');
      // throw new Error('didnt load table contents (opArtifactsTable)');
    }
    // TODO: the fact that we need to memoize this is a problem. The graph
    // itself should be cached instead.
    const res = memoJSONParse(contents);
    return res;
  },
  async (op, context) => {
    const outputNode: Graph.OutputNode = {
      nodeType: 'output',
      fromOp: op,
      type: null,
    };
    const columnTypesNode = opTableColumnTypes({table: outputNode as any});
    const columnTypes = (
      await Graph.executeForwardFresh(context, [columnTypesNode])
    )[0];
    return {...outputNode, type: {type: 'table' as const, columnTypes}};
  }
);

export const opFileJoinedTable = Graph.makeOp(
  'file-joinedtable',
  {file: {type: 'file', extension: 'json', wbObjectType: 'joined-table'}},
  {type: 'joined-table', columnTypes: {}},
  async (inputs, forwardGraph, forwardOp, context) => {
    const {file} = inputs;
    const {artifact, path} = file;
    const result = await context.backend.getFileContents(artifact.id, path);
    const contents = result.contents;
    if (contents == null) {
      return undefined;
      // throw new Error('didnt load table contents (opArtifactsTable)');
    }
    const joinedTable = JSON.parse(contents);
    return joinedTable;
  }
);

export const opJoinedTableRows = Graph.makeOp(
  'joinedtable-rows',
  {
    joinedTable: {type: 'joined-table', columnTypes: {}},
    leftOuter: 'boolean',
    rightOuter: 'boolean',
  },
  {type: 'array', objectType: 'any'},
  async (inputs, forwardGraph, forwardOp, context) => {
    // joinedTableRows is never resolved. Its replaced with other nodes
    // at graph construction time.
    throw new Error(
      'cant execute joinedtable-rows, ensure improveNode has been called!'
    );
  },
  async (op, context) => {
    const inputJoinedTableNode = op.inputs.joinedTable;
    if (inputJoinedTableNode.nodeType !== 'output') {
      throw new Error('invalid');
    }
    const joinedTable = (
      await Graph.executeForwardFresh(context, [inputJoinedTableNode])
    )[0];
    // TODO: Make sure this doesn't break anything
    const table1Path = joinedTable.table1;
    const table2Path = joinedTable.table2;
    const joinKey1 = joinedTable.join_key;
    const joinKey2 = joinedTable.join_key;

    const joinedTableOp = inputJoinedTableNode.fromOp;
    const inputFileNode = joinedTableOp.inputs.file;
    if (inputFileNode.nodeType !== 'output') {
      throw new Error('invalid');
    }

    const fileOp = inputFileNode.fromOp;
    if (fileOp.name !== 'artifact-file') {
      throw new Error('invalid');
    }
    const upstreamArtifactNode = fileOp.inputs.artifact;
    if (upstreamArtifactNode == null) {
      throw new Error('invalid');
    }

    async function getTableRowsNode(path: string) {
      const art = opArtifactFile({
        artifact: upstreamArtifactNode,
        path: constString(path),
      });
      let rowsNode: Graph.Node;
      // This switch seems like a smell, but leaving here for simplicity.
      if (path.endsWith('.partitioned-table.json')) {
        rowsNode = await HL.refineNodeType(
          context,
          opPartitionedTableRows({
            partitionedTable: opFilePartitionedTable({
              file: art as any,
            }),
          })
        );
      } else {
        rowsNode = opTableRows({
          table: (await HL.refineNodeType(
            context,
            opFileTable({
              file: art as any,
            })
          )) as any,
        });
      }
      return rowsNode;
    }

    const table1Rows = await getTableRowsNode(table1Path);
    const table2Rows = await getTableRowsNode(table2Path);

    return opJoin({
      arr1: table1Rows as any,
      arr2: table2Rows as any,
      join1Fn: constNodeUnsafe(
        table1Rows.type,
        opPick({
          obj: Graph.varNode(table1Rows.type, 'x') as any,
          key: constString(joinKey1),
        })
      ) as any,
      join2Fn: constNodeUnsafe(
        table2Rows.type,
        opPick({
          obj: Graph.varNode(table2Rows.type, 'x') as any,
          key: constString(joinKey2),
        })
      ) as any,
      alias1: constString('0'),
      alias2: constString('1'),
      leftOuter: op.inputs.leftOuter,
      rightOuter: op.inputs.rightOuter,
    });
  }
);

export const opFilePartitionedTable = Graph.makeOp(
  'file-partitionedtable',
  {file: {type: 'file', extension: 'json', wbObjectType: 'partitioned-table'}},
  {type: 'partitioned-table', columnTypes: {}},
  async (inputs, forwardGraph, forwardOp, context) => {
    const {file} = inputs;
    const {artifact, path} = file;
    const result = await context.backend.getFileContents(artifact.id, path);
    const contents = result.contents;
    if (contents == null) {
      return undefined;
    }
    const partitionedTable = JSON.parse(contents);
    return partitionedTable;
  }
);

export const opPartitionedTableRows = Graph.makeOp(
  'partitionedtable-rows',
  {partitionedTable: {type: 'partitioned-table', columnTypes: {}}},
  {type: 'array', objectType: 'any'},
  async (inputs, forwardGraph, forwardOp, context) => {
    // partitionedTableRows is never resolved. Its replaced with other nodes
    // at graph construction time.
    throw new Error(
      'cant execute partitionedtable-rows, ensure improveNode has been called!'
    );
  },
  async (op, context) => {
    const inputPartitionedTableNode = op.inputs.partitionedTable;
    if (inputPartitionedTableNode.nodeType !== 'output') {
      throw new Error('invalid');
    }
    const partitionedTable = (
      await Graph.executeForwardFresh(context, [inputPartitionedTableNode])
    )[0];
    const partsPath = partitionedTable.parts_path;
    const partitionedTableOp = inputPartitionedTableNode.fromOp;

    const inputFileNode = partitionedTableOp.inputs.file;
    if (inputFileNode.nodeType !== 'output') {
      throw new Error('invalid');
    }

    const fileOp = inputFileNode.fromOp;
    if (fileOp.name !== 'artifact-file') {
      throw new Error('invalid');
    }
    const upstreamArtifactNode = fileOp.inputs.artifact;
    if (upstreamArtifactNode == null) {
      throw new Error('invalid');
    }
    // Note: this feels wrong that i have to resolve this to pass to the opFileDir
    const artifact = (
      await Graph.executeForwardFresh(context, [upstreamArtifactNode])
    )[0];
    const fileDirNode = constNodeUnsafe(
      {type: 'dir'},
      {artifact, path: partsPath as string}
    );
    const files = opFileDir({file: fileDirNode as any});
    const partTables = (await Graph.executeForwardFresh(context, [files]))[0]
      .files;
    const tableNodes = Object.keys(partTables)
      .filter(key => key.endsWith('table.json'))
      .map(key => {
        return opFileTable({
          file: opArtifactFile({
            artifact: upstreamArtifactNode,
            path: constString(partTables[key].fullPath),
          }) as any,
        });
      });
    const improvedTableNodes = await Promise.all(
      tableNodes.map(node => {
        return HL.refineNodeType(context, node);
      })
    );
    // TODO: Assert homogenous column types
    const tableRowNodes = improvedTableNodes.map(node => {
      return opTableRows({table: node as any});
    });
    const tableRowArrayNode = opArray(
      _.fromPairs(tableRowNodes.map((node, ndx) => [ndx, node])) as any
    );
    return opUnion({
      arr: tableRowArrayNode as any,
    });
  }
);

export const opFileContents = Graph.makeOp(
  'file-contents',
  {file: {type: 'file'}},
  'string',
  async (inputs, forwardGraph, forwardOp, context) => {
    const {file} = inputs;
    const {artifact, path} = file;
    const result = await context.backend.getFileContents(artifact.id, path);
    const contents = result.contents;
    if (contents == null) {
      return undefined;
      // throw new Error('didnt load table contents (opArtifactsTable)');
    }
    return contents;
  }
);

export const opFileMediaFile = Graph.makeOp(
  'file-mediafile',
  {file: {type: 'file'}},
  inputs => {
    return inputs.file.type.wbObjectType;
  },
  async (inputs, forwardGraph, forwardOp, context) => {
    const {file} = inputs;
    const {artifact, path} = file;
    const result = await context.backend.getFileContents(artifact.id, path);
    const contents = result.contents;
    if (contents == null) {
      return undefined;
    }
    return JSON.parse(contents);
  }
);

export const opFileDirectUrl = Graph.makeOp(
  'file-directUrl',
  {file: {type: 'file'}},
  'string',
  async (inputs, forwardGraph, forwardOp, context) => {
    const {file} = inputs;
    const {artifact, path} = file;
    const result = await context.backend.getFileDirectUrl(artifact.id, path);
    return result.directUrl;
  }
);

export const opTableColumnTypes = Graph.makeOp(
  'table-columnTypes',
  {table: {type: 'table', columnTypes: {}}},
  // Any?
  {type: 'dict', objectType: 'any'},
  inputs => {
    const {table} = inputs;
    if (table == null) {
      return {};
    }
    const colTypes = MediaTable.detectColumnTypes(table);
    const result: {[key: string]: Types.Type} = {};
    for (let i = 0; i < table.columns.length; i++) {
      result[table.columns[i]] = colTypes[i];
    }
    return result;
  }
);

export const opTableRows = Graph.makeOp(
  'table-rows',
  {table: {type: 'table', columnTypes: {}}},
  inputs => {
    return {
      type: 'array',
      objectType: {
        type: 'object',
        propertyTypes: inputs.table.type.columnTypes,
      },
    };
  },
  async (inputs, forwardGraph, forwardOp, context) => {
    const {table} = inputs;
    const peerTables: {[column: string]: any[]} = {};
    if (table.column_types) {
      const tableArtifactNode = opTableArtifact({
        asset: forwardOp.op.inputs.table,
      });
      const types = table.column_types.params.type_map;
      for (const colName of table.columns) {
        if (types[colName].wb_type === 'wandb.TableForeignIndex') {
          peerTables[colName] = (
            await Graph.executeForwardFresh(context, [
              opTableRows({
                table: opFileTable({
                  file: opArtifactFile({
                    artifact: tableArtifactNode as any,
                    path: constString(types[colName].params.table),
                  }) as any,
                }),
              }),
            ])
          )[0];
        }
      }
    }
    return table.data.map((row: any) => {
      const rowResult: {[key: string]: any} = {};
      for (let i = 0; i < table.columns.length; i++) {
        if (table.columns[i] in peerTables) {
          rowResult[table.columns[i]] = peerTables[table.columns[i]][row[i]];
        } else {
          rowResult[table.columns[i]] = row[i];
        }
      }
      return rowResult;
    });
  },
  async (op, context) => {
    const propertyTypes: {[key: string]: any} = {};
    Object.keys(op.inputs.table.type.columnTypes).forEach(ct => {
      propertyTypes[ct] = op.inputs.table.type.columnTypes[ct];
    });
    const table = (
      await Graph.executeForwardFresh(context, [op.inputs.table])
    )[0];
    if (table.column_types) {
      const tableArtifactNode = opTableArtifact({asset: op.inputs.table});
      const types = table.column_types.params.type_map;
      for (const colName of table.columns) {
        if (types[colName].wb_type === 'wandb.TableForeignIndex') {
          const peerTableRowsNode = opTableRows({
            table: opFileTable({
              file: opArtifactFile({
                artifact: tableArtifactNode as any,
                path: constString(types[colName].params.table),
              }) as any,
            }),
          });
          const peerTableRowsNodeRefined = await HL.refineNodeType(
            context,
            peerTableRowsNode
          );
          if (
            peerTableRowsNodeRefined &&
            peerTableRowsNodeRefined.type &&
            Types.isArray(peerTableRowsNodeRefined.type)
          ) {
            propertyTypes[colName] = peerTableRowsNodeRefined.type.objectType;
          }
        }
      }
    }
    return {
      nodeType: 'output',
      type: {
        type: 'array',
        objectType: {
          type: 'object',
          propertyTypes,
        },
      },
      fromOp: op,
    };
  }
);

export const opTableRowKeys = Graph.makeOp(
  'tablerow-keys',
  {tableRow: 'table-row'},
  {type: 'array', objectType: 'string'},
  inputs => {
    const {tableRow} = inputs;
    return Object.keys(tableRow);
  }
);

const assetArtifactResolver: Graph.OpResolverFn = async (
  inputs,
  forwardGraph,
  forwardOp,
  context
) => {
  let node = forwardOp.op.inputs.asset;
  let oldestPickOp: Graph.Op | undefined;
  // Kinda nuts, walk up through the graph to figure out where
  // we came from.
  // TODO: At least make some helper functions to make this easier
  // to read (and do in other places)
  // TODO: Surely this is wrong and doesn't work in the general case.

  // First, walk up the op chain until we find a file type node, or
  // a join op, keeping track of the oldest pick op along the way.
  while (node.nodeType === 'output') {
    if (!Types.isSimpleType(node.type) && node.type.type === 'file') {
      break;
    }
    const op = node.fromOp;
    if (op.name === 'pick') {
      oldestPickOp = op;
    } else if (op.name === 'join') {
      break;
    } else if (op.name === 'table-rows') {
      // This works in my base case, but need to think about how to make more general
      if (oldestPickOp) {
        const colName = (oldestPickOp.inputs.key as Graph.ConstNode).val.split(
          '.'
        )[0];
        const table = HL.getNodeResult(forwardGraph, op.inputs.table);
        if (
          table &&
          table.column_types &&
          table.column_types.params &&
          table.column_types.params.type_map &&
          table.column_types.params.type_map[colName]
        ) {
          const colType = table.column_types.params.type_map[colName];
          if (colType.wb_type === 'wandb.TableForeignIndex') {
            const ogTableArtifactNode = opTableArtifact({
              asset: op.inputs.table,
            }) as any;
            const peerTableArtifactFileNode = opArtifactFile({
              artifact: ogTableArtifactNode,
              path: constString(colType.params.table),
            });
            const peerTableFileNode = opFileTable({
              file: peerTableArtifactFileNode as any,
            });
            const peerTableArtifactNode = opTableArtifact({
              asset: peerTableFileNode,
            });
            const res = await Graph.executeForwardFresh(context, [
              peerTableArtifactNode,
            ]);
            return res[0];
          }
        }
      }
    }
    node = Object.values(op.inputs)[0];
  }

  // If we stopped at a join up, walk up the join ops, taking the appropriate
  // fork based on our pick key, as we encounter them.
  if (node.nodeType === 'output' && node.fromOp.name === 'join') {
    if (oldestPickOp == null) {
      throw new Error('invalid');
    }
    const keyNode = forwardGraph.ops.get(oldestPickOp)?.op.inputs.key!;
    const pickKey: string = HL.getNodeResult(forwardGraph, keyNode);
    const pickKeyFields = pickKey.split('.');
    const aliases = pickKeyFields.slice(0, pickKeyFields.length - 1);
    while (node.nodeType === 'output') {
      if (!Types.isSimpleType(node.type) && node.type.type === 'file') {
        break;
      }
      const op: Graph.Op = node.fromOp;
      if (op.name === 'join') {
        const alias = aliases.shift();
        if (alias == null) {
          throw new Error('invalid');
        }
        const alias1 = HL.getNodeResult(forwardGraph, op.inputs.alias1);
        const alias2 = HL.getNodeResult(forwardGraph, op.inputs.alias2);
        if (alias === alias1) {
          node = op.inputs.arr1;
        } else if (alias === alias2) {
          node = op.inputs.arr2;
        } else {
          throw new Error('invalid');
        }
      } else {
        node = Object.values(op.inputs)[0];
      }
    }
  }

  // Finally, we should have reach a file node. From here we can return
  // an artifact object.
  if (Types.isSimpleType(node.type) || node.type.type !== 'file') {
    throw new Error('INVALID');
  }
  const fileNodeResult = HL.getNodeResult(forwardGraph, node);
  const metadata = await context.backend.getFileMetadata(
    fileNodeResult.artifact.id,
    fileNodeResult.path
  );
  if (metadata == null || metadata.type !== 'file') {
    throw new Error('invalid');
  }
  if (metadata.ref == null) {
    return {id: fileNodeResult.artifact.id};
  }
  const {artifactId} = File.parseArtifactRef(metadata.ref);
  return {id: artifactId};
};

// TODO: This needs a test!
export const opImageArtifact = Graph.makeOp(
  'imagefile-artifact',
  {asset: 'image-file'},
  'artifact',
  assetArtifactResolver
);

export const opVideoArtifact = Graph.makeOp(
  'videofile-artifact',
  {asset: 'video-file'},
  'artifact',
  assetArtifactResolver
);

export const opAudioArtifact = Graph.makeOp(
  'audiofile-artifact',
  {asset: 'audio-file'},
  'artifact',
  assetArtifactResolver
);

export const opHtmlArtifact = Graph.makeOp(
  'htmlfile-artifact',
  {asset: 'html-file'},
  'artifact',
  assetArtifactResolver
);

export const opBokehArtifact = Graph.makeOp(
  'bokehfile-artifact',
  {asset: 'bokeh-file'},
  'artifact',
  assetArtifactResolver
);

export const opObject3DArtifact = Graph.makeOp(
  'object3Dfile-artifact',
  {asset: 'object3D-file'},
  'artifact',
  assetArtifactResolver
);

export const opTableArtifact = Graph.makeOp(
  'table-artifact',
  {asset: {type: 'table', columnTypes: {}}},
  'artifact',
  assetArtifactResolver
);

export const opCount = Graph.makeOp(
  'count',
  {arr: {type: 'array', objectType: 'any'}},
  'number',
  inputs => {
    const {arr} = inputs;
    if (arr == null) {
      // TODO: WHAT THE HELL, I needed to do this to remove a glitch.
      // Not sure where the glitching is coming from. Its an interaction
      // between the useEach of a Multi-container embedded in PanelTable
      // which also does a useEach
      return 0;
    }
    return arr.length;
  }
);

export const opIndex = Graph.makeOp(
  'index',
  {arr: {type: 'array', objectType: 'any'}, index: 'number'},
  inputs => {
    return inputs.arr.type.objectType;
  },
  inputs => {
    const {arr, index} = inputs;
    return arr[index];
  }
);

export const opOffset = Graph.makeOp(
  'offset',
  {arr: {type: 'array', objectType: 'any'}, offset: 'number'},
  inputs => {
    return inputs.arr.type;
  },
  inputs => {
    const {arr, offset} = inputs;
    return arr.slice(offset);
  }
);

export const opLimit = Graph.makeOp(
  'limit',
  {arr: {type: 'array', objectType: 'any'}, limit: 'number'},
  inputs => {
    return inputs.arr.type;
  },
  inputs => {
    const {arr, limit} = inputs;
    return arr.slice(0, limit);
  }
);

const starGetInner = (
  obj: any,
  pathParts: string[]
): any | {[key: string]: any} => {
  if (obj == null) {
    return null;
  }
  // Skip tagged object containers.
  // TODO: what if the user has t & v keys!? This should be done
  // by type inspection rather than value inspection.
  if (obj.t != null && obj.v != null) {
    obj = obj.v;
  }
  if (pathParts.length === 0) {
    return obj;
  }
  const pp0 = pathParts[0];
  if (pp0 === '*') {
    const result: {[key: string]: any} = {};
    for (const k of Object.keys(obj)) {
      result[k] = starGetInner(obj[k], pathParts.slice(1));
    }
    return result;
  }
  return starGetInner(obj[pp0], pathParts.slice(1));
};

const flattenObj = (
  obj: any,
  remainingLevels: number
): {[key: string]: any} => {
  const result: {[key: string]: any} = {};
  for (const k of Object.keys(obj)) {
    if (remainingLevels === 1) {
      result[k] = obj[k];
    } else {
      const inner = flattenObj(obj[k] ?? {}, remainingLevels - 1);
      for (const innerK of Object.keys(inner)) {
        result[k + '.' + innerK] = inner[innerK];
      }
    }
  }
  return result;
};

const starGet = (obj: any, path: string) => {
  const pathParts = path.split('.');
  const result = starGetInner(obj, pathParts);
  const starCount = pathParts.filter(pp => pp === '*').length;
  if (starCount !== 0) {
    return flattenObj(result, starCount);
  }
  return result;
};

export const opPick = Graph.makeOp(
  'pick',
  {
    obj: Types.oneOrMany(Types.object({})),
    key: 'string',
  },
  'any',
  inputs => {
    let {obj} = inputs;
    const {key} = inputs;
    if (obj.t != null && obj.v != null) {
      obj = obj.v;
    }
    // TODO: Get access to op input types instead of value type checking
    if (_.isArray(obj)) {
      return obj.map((r: any) => starGet(r, key) ?? null);
    }
    if (obj == null) {
      return null;
    }
    const value = starGet(obj, key);
    if (value == null) {
      return null;
    }
    return value;
  },
  async (op, context) => {
    const keyValue = (
      await Graph.executeForwardFresh(context, [op.inputs.key])
    )[0];
    const objType = op.inputs.obj.type as Types.Type;
    const subKeys = keyValue.split('.');
    if (Types.isSimpleType(objType)) {
      throw new Error(
        `invalid type for pick ${keyValue}: ${Types.toString(objType)}`
      );
    }
    let type: Types.Type = objType;
    if (Types.isTaggedValue(type)) {
      type = type.value;
    }
    let resultArray = false;
    if (Types.isArray(type)) {
      resultArray = true;
      type = type.objectType;
    }
    let resultNullable = false;
    let multiple = false;
    for (const k of subKeys) {
      if (Types.isTaggedValue(type)) {
        type = type.value;
      }
      if (Types.isSimpleType(type)) {
        throw new Error(
          `invalid type for pick ${keyValue}: ${Types.toString(objType)}`
        );
      }
      if (type.type === 'union') {
        const objMembers = type.members.filter(
          t => !Types.isSimpleType(t) && t.type === 'object'
        ) as Types.ObjectType[];
        if (objMembers.length !== type.members.length) {
          resultNullable = true;
        }
        const mergedProperties: {[key: string]: Types.Type} = {};
        for (const key of _.union(
          ...objMembers.map(m => Object.keys(m.propertyTypes))
        )) {
          mergedProperties[key] = Types.union(
            objMembers.map(m => m.propertyTypes[key] ?? null)
          );
        }
        type = {
          type: 'object',
          propertyTypes: mergedProperties,
        };
      }
      if (type.type !== 'object') {
        throw new Error(
          `invalid type for pick ${keyValue}: ${Types.toString(objType)}`
        );
      }
      if (k === '*') {
        multiple = true;
        type = Types.union(Object.values(type.propertyTypes));
      } else {
        type = type.propertyTypes[k];
        if (type == null) {
          return {nodeType: 'output', type: null, fromOp: op};
        }
      }
    }
    if (resultNullable) {
      type = Types.union([null, type]);
    }
    if (multiple) {
      type = {type: 'dict', objectType: type};
    }
    if (resultArray) {
      type = {
        type: 'array',
        objectType: type,
      };
    }
    return {nodeType: 'output', type, fromOp: op};
  }
);

const compareItems = (a: any, b: any): number => {
  if (_.isArray(a)) {
    for (let i = 0; i < a.length; i++) {
      const res = compareItems(a[i], b[i]);
      if (res !== 0) {
        return res;
      }
    }
  } else {
    if (a == null || a < b) {
      return -1;
    } else if (b == null || a > b) {
      return 1;
    }
  }
  return 0;
};

export const opSort = Graph.makeOp(
  'sort',
  {
    arr: {type: 'array', objectType: 'any'},
    comparableDataFn: {type: 'function', inputType: 'any', outputType: 'any'},
    columnDirs: {type: 'array', objectType: 'string'},
  },
  inputs => {
    const {arr} = inputs;
    return arr.type;
  },
  async (inputs, forwardGraph, forwardOp, context) => {
    const calls: Graph.Node[] = inputs.arr.map((x: any) =>
      HL.callFunction(inputs.comparableDataFn, {x: constNodeUnsafe('any', x)})
    );
    const comparableObjs = await Graph.executeForwardFresh(context, calls);
    return (inputs.arr as any[])
      .map((row, ndx) => {
        return {
          row,
          comparableObj: comparableObjs[ndx],
        };
      })
      .sort((rowA, rowB) => {
        for (let i = 0; i < inputs.columnDirs.length; i++) {
          const comp = compareItems(
            rowA.comparableObj[i],
            rowB.comparableObj[i]
          );
          if (comp !== 0) {
            return comp * (inputs.columnDirs[i] === 'asc' ? 1 : -1);
          }
        }
        return 0;
      })
      .map(item => {
        return item.row;
      });
  }
);

export const opFilter = Graph.makeOp(
  'filter',
  {
    arr: {type: 'array', objectType: 'any'},
    filterFn: {type: 'function', inputType: 'any', outputType: 'boolean'},
  },
  inputs => {
    const {arr} = inputs;
    return arr.type;
  },
  async (inputs, forwardGraph, forwardOp, context) => {
    const arr = inputs.arr;
    const filterFn = inputs.filterFn;
    const calls: Graph.Node[] = arr.map((x: any) =>
      HL.callFunction(filterFn, {x: constNodeUnsafe('any', x)})
    );

    const result: any[] = [];
    const predicateResult = await Graph.executeForwardFresh(context, calls);
    for (let i = 0; i < predicateResult.length; i++) {
      const predRes = predicateResult[i];
      const val = arr[i];
      if (predRes) {
        result.push(val);
      }
    }
    return result;
  }
);

export const opGroupBy = Graph.makeOp(
  'groupBy',
  {
    arr: {type: 'array', objectType: 'any'},
    groupByFn: {type: 'function', inputType: 'any', outputType: 'any'},
  },
  inputs => {
    const {arr, groupByFn} = inputs;
    const objectType = arr.type.objectType;
    return Types.array(
      Types.taggedValue(groupByFn.type, Types.array(objectType))
    );
  },
  async (inputs, forwardGraph, forwardOp, context) => {
    const tableRows = inputs.arr;
    const groupByFn = inputs.groupByFn;
    // console.time('opGroupBy-construct-calls');
    const calls: Graph.Node[] = tableRows.map((x: any) =>
      HL.callFunction(groupByFn, {
        x: constNodeUnsafe({type: 'object', propertyTypes: {}}, x),
      })
    );
    const groupKeys = (await Graph.executeForwardFresh(context, calls)).map(
      (x: string | null | undefined) => x ?? ''
    );
    const result: {[key: string]: {t: any; v: any[]}} = {};
    for (let i = 0; i < groupKeys.length; i++) {
      // Converting to string to allow nested grouping
      let groupKey: string;
      if (_.isObject(groupKeys[i])) {
        groupKey = JSON.stringify(groupKeys[i]);
      } else {
        groupKey = groupKeys[i];
      }
      const tableRow = tableRows[i];
      if (result[groupKey] == null) {
        result[groupKey] = {t: groupKeys[i], v: []};
      }
      result[groupKey].v.push(tableRow);
    }
    return Object.values(result);
  }
);

export const opGetTag = Graph.makeOp(
  'get-tag',
  {obj: Types.oneOrMany(Types.taggedValue('any', 'any'))},
  inputs => {
    const {obj} = inputs;
    const objType = obj.type;
    if (Types.isArray(objType)) {
      if (!Types.isTaggedValue(objType.objectType)) {
        throw new Error('invalid');
      }
      return Types.array(objType.objectType.tag);
    }
    return objType.tag;
  },
  inputs => {
    const {obj} = inputs;
    if (_.isArray(obj)) {
      return obj.map(o => o.t);
    }
    return obj.t;
  }
);

// This is an outer join
export const opJoin = Graph.makeOp(
  'join',
  {
    arr1: {type: 'array', objectType: 'any'},
    arr2: {type: 'array', objectType: 'any'},
    join1Fn: {type: 'function', inputType: 'any', outputType: 'any'},
    join2Fn: {type: 'function', inputType: 'any', outputType: 'any'},
    alias1: 'string',
    alias2: 'string',
    leftOuter: 'boolean',
    rightOuter: 'boolean',
  },
  inputs => {
    const {arr1, arr2, alias1, alias2} = inputs;
    if (alias1.nodeType !== 'const' || alias2.nodeType !== 'const') {
      // TODO:
      //   this needs to be resolved. Need to refactor to allow async
      //   ops, while still retaining ability to one-shot entire graph.
      throw new Error('opJoin only supports const aliases');
    }
    const arr1ObjectType = arr1.type.objectType;
    const arr2ObjectType = arr2.type.objectType;
    return {
      type: 'array',
      objectType: {
        type: 'object',
        propertyTypes: {
          [alias1.val]: {
            type: 'union',
            members: [null, arr1ObjectType],
          },
          [alias2.val]: {
            type: 'union',
            members: [null, arr2ObjectType],
          },
        },
      },
    } as any;
  },
  async (inputs, forwardGraph, forwardOp, context) => {
    const {
      arr1,
      arr2,
      join1Fn,
      join2Fn,
      alias1,
      alias2,
      leftOuter,
      rightOuter,
    } = inputs;
    let calls: Graph.Node[] = arr1.map((x: any) =>
      HL.callFunction(join1Fn, {
        x: constNodeUnsafe({type: 'object', propertyTypes: {}}, x),
      })
    );
    calls = calls.concat(
      arr2.map((x: any) =>
        HL.callFunction(join2Fn, {
          x: constNodeUnsafe({type: 'object', propertyTypes: {}}, x),
        })
      )
    );

    const joinKeys = await Graph.executeForwardFresh(context, calls);
    const arr1JoinKeys = joinKeys.slice(0, arr1.length);
    const arr2JoinKeys = joinKeys.slice(arr1.length);

    const listToDict = (
      prev: {[key: string]: number[]},
      curr: any,
      ndx: number
    ) => {
      if (!prev[curr]) {
        prev[curr] = [];
      }
      prev[curr].push(ndx);
      return prev;
    };
    const arr1JoinKeyLookup = arr1JoinKeys.reduce(listToDict, {});
    const arr2JoinKeyLookup = arr2JoinKeys.reduce(listToDict, {});
    let allJoinKeys: any[];
    if (leftOuter && rightOuter) {
      allJoinKeys = _.union(arr1JoinKeys, arr2JoinKeys);
    } else if (leftOuter) {
      allJoinKeys = _.uniq(arr1JoinKeys);
    } else if (rightOuter) {
      allJoinKeys = _.uniq(arr2JoinKeys);
    } else {
      allJoinKeys = _.intersection(arr1JoinKeys, arr2JoinKeys);
    }
    const result: any[] = [];
    for (const jk of allJoinKeys) {
      let arr1Indexes: Array<number | null> = arr1JoinKeyLookup[jk];
      let arr2Indexes: Array<number | null> = arr2JoinKeyLookup[jk];
      if (rightOuter && !arr1Indexes) {
        arr1Indexes = [null];
      }
      if (leftOuter && !arr2Indexes) {
        arr2Indexes = [null];
      }
      for (const arr1Index of arr1Indexes) {
        for (const arr2Index of arr2Indexes) {
          result.push({
            [alias1]: arr1Index != null ? arr1[arr1Index!] : null,
            [alias2]: arr2Index != null ? arr2[arr2Index!] : null,
          });
        }
      }
    }
    return result;
  }
);

export const opUnion = Graph.makeOp(
  'union',
  {
    arr: {type: 'array', objectType: {type: 'array', objectType: 'any'}},
  },
  inputs => {
    const {arr} = inputs;
    return arr.type.objectType;
  },
  async (inputs, forwardGraph, forwardOp, context) => {
    return _.union(...inputs.arr);
  }
);

export const opArtifactStars = Graph.makeOp(
  'artifact-stars',
  {artifact: 'artifact'},
  'number',
  inputs => {
    const {artifact} = inputs;
    return artifact.stars;
  }
);

// xx
export const opProjectRuns = Graph.makeOp(
  'project-runs',
  {project: 'project'},
  {type: 'array', objectType: 'run'}
);

export const opRunsFilter = Graph.makeOp(
  'runs-filter',
  {runs: {type: 'array', objectType: 'run'}, filter: 'filter'},
  {type: 'array', objectType: 'run'}
);

export const opRunsId = Graph.makeOp(
  'runs-id',
  {runs: {type: 'array', objectType: 'run'}},
  {type: 'array', objectType: 'number'},
  inputs => {
    const {runs} = inputs;
    return _.map(runs, 'id');
  }
);

// TODO: definitely not final
export const opRunsSummary = Graph.makeOp(
  'runs-summary',
  {runs: {type: 'array', objectType: 'run'}, key: 'string'},
  {type: 'array', objectType: 'number'},
  inputs => {
    const {runs, key} = inputs;
    const res = _.map(runs, o => _.get(o, ['summary', key]));
    return res;
  }
);

export const opRunsStars = Graph.makeOp(
  'runs-stars',
  {runs: {type: 'array', objectType: 'run'}},
  {type: 'array', objectType: 'number'},
  inputs => {
    const {runs} = inputs;
    return _.map(runs, 'stars');
  }
);

export const opRunsGroupBy = Graph.makeOp(
  'runs-groupby',
  {
    runs: {type: 'array', objectType: 'run'},
    fn: {type: 'function', inputType: 'run', outputType: 'string'},
  },
  {type: 'dict', objectType: {type: 'array', objectType: 'run'}},
  async (inputs, forwardGraph, forwardOp, context) => {
    const runs = inputs.runs;
    const fn = inputs.fn;
    const calls: Graph.Node[] = runs.map((run: any) =>
      HL.callFunction(fn, {x: constNodeUnsafe('run', run)})
    );

    const result: {[key: string]: any[]} = {};
    const groupKeys = await Graph.executeForwardFresh(context, calls);
    for (let i = 0; i < groupKeys.length; i++) {
      const groupKey = groupKeys[i];
      const run = runs[i];
      if (result[groupKey] == null) {
        result[groupKey] = [];
      }
      result[groupKey].push(run);
    }
    return result;
  }
);

export const opRunsMap = Graph.makeOp(
  'runs-map',
  {
    runs: {type: 'array', objectType: 'run'},
    fn: {type: 'function', inputType: 'run', outputType: 'number'},
  },
  {type: 'array', objectType: 'number'},
  async (inputs, forwardGraph, forwardOp, context) => {
    const runs = inputs.runs;
    const fn = inputs.fn;

    const calls: Graph.Node[] = runs.map((run: any) =>
      HL.callFunction(fn, {x: constNodeUnsafe('run', run)})
    );

    return Graph.executeForwardFresh(context, calls);
  }
);

export const opRunId = Graph.makeOp('run-id', {run: 'run'}, 'string');
export const opRunName = Graph.makeOp('run-name', {run: 'run'}, 'string');
export const opRunSummary = Graph.makeOp(
  'run-summary',
  {run: 'run', key: 'string'},
  'number'
);

export const opRunStars = Graph.makeOp('run-stars', {run: 'run'}, 'number');

export const opNumbersAvg = Graph.makeOp(
  'numbers-avg',
  {from: Types.array(Types.maybe('number'))},
  'number',
  inputs => {
    const numbers = inputs.from.filter((num: number) => num != null);
    return _.sum(numbers) / numbers.length;
  }
);

export const opNumbersMin = Graph.makeOp(
  'numbers-min',
  {from: Types.array(Types.maybe('number'))},
  'number',
  inputs => {
    const numbers = inputs.from.filter((num: number) => num != null);
    return _.min(numbers);
  }
);

export const opNumbersMax = Graph.makeOp(
  'numbers-max',
  {from: Types.array(Types.maybe('number'))},
  'number',
  inputs => {
    const numbers = inputs.from.filter((num: number) => num != null);
    return _.max(numbers);
  }
);

const makeProjectedComparatorResolverFn: (
  fn: (a: any, b: any) => any
) => Graph.OpResolverFn = fn => {
  const innerFn: Graph.OpResolverFn = inputs => {
    const {lhs, rhs} = inputs;
    // Note: we unwrap tagged values in this function call every time.
    // If we know the input type here we can more efficiently compute a function
    // up front
    const comparatorFn = (l: null | number | {v: number}, r: number) =>
      l != null ? (_.isObject(l) ? fn(l.v, rhs) : fn(l, r)) : null;
    // TODO: This only supports single level arrays currently!
    if (Array.isArray(lhs)) {
      return lhs.map(v => comparatorFn(v, rhs));
    }
    return comparatorFn(lhs, rhs);
  };
  return innerFn;
};

export const opNumberAdd = Graph.makeOp(
  'number-add',
  {lhs: Types.oneOrMany(Types.union(['number', null])), rhs: 'number'},
  inputs => {
    const {lhs} = inputs;
    return lhs.type;
  },
  makeProjectedComparatorResolverFn((a, b) => a + b)
);

export const opNumberSub = Graph.makeOp(
  'number-sub',
  {lhs: Types.oneOrMany(Types.union(['number', null])), rhs: 'number'},
  inputs => {
    const {lhs} = inputs;
    return lhs.type;
  },
  makeProjectedComparatorResolverFn((a, b) => a - b)
);

export const opNumberMult = Graph.makeOp(
  'number-mult',
  {lhs: Types.oneOrMany(Types.union(['number', null])), rhs: 'number'},
  inputs => {
    const {lhs} = inputs;
    return lhs.type;
  },
  makeProjectedComparatorResolverFn((a, b) => a * b)
);

export const opNumberDiv = Graph.makeOp(
  'number-div',
  {lhs: Types.oneOrMany(Types.union(['number', null])), rhs: 'number'},
  inputs => {
    const {lhs} = inputs;
    return lhs.type;
  },
  makeProjectedComparatorResolverFn((a, b) => a / b)
);

const projectedComparatorTypeFn: Graph.TypeFn<any> = inputs => {
  const {lhs} = inputs;
  let array = false;
  let innerType = lhs.type;
  if (Types.isArray(innerType)) {
    array = true;
    innerType = innerType.objectType;
  }
  let result: Types.Type = 'boolean';
  if (Types.isNullable(innerType)) {
    result = Types.maybe(result);
  }
  if (array) {
    result = Types.array(result);
  }
  return result;
};

export const opNumberEqual = Graph.makeOp(
  'number-equal',
  {lhs: Types.oneOrMany(Types.union(['number', null])), rhs: 'number'},
  projectedComparatorTypeFn,
  makeProjectedComparatorResolverFn((a, b) => a === b)
);

export const opNumberNotEqual = Graph.makeOp(
  'number-notequal',
  {lhs: Types.oneOrMany(Types.union(['number', null])), rhs: 'number'},
  projectedComparatorTypeFn,
  makeProjectedComparatorResolverFn((a, b) => a !== b)
);

export const opNumberGreater = Graph.makeOp(
  'number-greater',
  {lhs: Types.oneOrMany(Types.union(['number', null])), rhs: 'number'},
  projectedComparatorTypeFn,
  makeProjectedComparatorResolverFn((a, b) => a > b)
);

export const opNumberLess = Graph.makeOp(
  'number-less',
  {lhs: Types.oneOrMany(Types.union(['number', null])), rhs: 'number'},
  projectedComparatorTypeFn,
  makeProjectedComparatorResolverFn((a, b) => a < b)
);

// commenting out to make autocomplete easier -- may want to revisit later by
// making autocomplete able to block resolution when there are multiple matches

// export const opNumberLessEqual = Graph.makeOp(
//   'number-less-equal',
//   {lhs: Types.oneOrMany(Types.union(['number', null])), rhs: 'number'},
//   projectedComparatorTypeFn,
//   makeProjectedComparatorResolverFn((a, b) => a <= b)
// );

// export const opNumberGreaterEqual = Graph.makeOp(
//   'number-greater-equal',
//   {lhs: Types.oneOrMany(Types.union(['number', null])), rhs: 'number'},
//   projectedComparatorTypeFn,
//   makeProjectedComparatorResolverFn((a, b) => a >= b)
// );

export const opStringEqual = Graph.makeOp(
  'string-equal',
  {lhs: Types.oneOrMany(Types.union(['string', null])), rhs: 'string'},
  projectedComparatorTypeFn,
  makeProjectedComparatorResolverFn((a, b) => a === b)
);

export const opStringNotEqual = Graph.makeOp(
  'string-notequal',
  {lhs: Types.oneOrMany(Types.union(['string', null])), rhs: 'string'},
  projectedComparatorTypeFn,
  makeProjectedComparatorResolverFn((a, b) => a !== b)
);

export const opAnd = Graph.makeOp(
  'and',
  // NOTE: this actually can handle more than two objects. But the editor
  // does not
  // TODO: This will break the graph view. Figure out a way to specify this.
  {lhs: 'boolean', rhs: 'boolean'},
  'boolean',
  inputs => {
    // Inputs are all arguments
    const vals = Object.values(inputs);
    // TODO: This doesn't short circuit
    return vals.every((o: any) => o);
  }
);

export const opDictToArray = Graph.makeOp(
  'dict-to-array',
  {dict: {type: 'dict', objectType: 'any'}},
  inputs => {
    return {type: 'array', objectType: inputs.dict.type};
  },
  inputs => {
    return Object.values(inputs.dict);
  }
);

export const opOr = Graph.makeOp(
  'or',
  // NOTE: this actually can handle more than two objects. But the editor
  // does not
  // TODO: This will break the graph view. Figure out a way to specify this.
  {lhs: 'boolean', rhs: 'boolean'},
  'boolean',
  inputs => {
    // Inputs are all arguments
    const vals = Object.values(inputs);
    // TODO: This doesn't short circuit
    return vals.some((o: any) => o);
  }
);

export const opArray = Graph.makeOp(
  'array',
  // This takes a variable number of arguments, but we only specify one...
  // TODO: This will break the graph view. Figure out a way to specify this.
  // Using void type so these opArray doesn't show up in auto-suggest for now.
  {manyX: 'void'},
  inputs => {
    const argValues = Object.values(inputs);
    if (argValues.length === 0) {
      throw new Error('opArray requires at least one input');
    }
    // I added the av.type !== null to allow for null types in the array
    if (
      argValues
        .slice(1)
        .some(av => av.type !== null && !_.isEqual(av.type, argValues[0].type))
    ) {
      throw new Error('array member types must be homogenous');
    }
    return {type: 'array', objectType: argValues[0].type};
  },
  inputs => {
    // Inputs are all arguments
    return Object.values(inputs);
  }
);

// TODO: Figure out what to do with these array ops
export const opArrayUnsafe = Graph.makeOp(
  'array-unsafe',
  // This takes a variable number of arguments, but we only specify one...
  // TODO: This will break the graph view. Figure out a way to specify this.
  // Using void type so these opArray doesn't show up in auto-suggest for now.
  {manyX: 'void'},
  inputs => {
    const argValues = Object.values(inputs);
    if (argValues.length === 0) {
      throw new Error('opArray requires at least one input');
    }
    return {type: 'array', objectType: 'any'};
  },
  inputs => {
    // Inputs are all arguments
    return Object.values(inputs);
  }
);

export const spread = <T extends Types.Type>(
  arrayArgs: T[]
): {[argName: string]: T} => {
  const result: {[argName: string]: T} = {};
  arrayArgs.forEach((v, i) => (result[i] = v));
  return result;
};

export const oneOrMany = <T extends Types.Type>(items: T[]) => {
  if (items.length === 1) {
    return items[0];
  }
  return opArray(spread(items) as any);
};

export const applyOpToOneOrMany = (
  opFn: any,
  firstArgName: string,
  items: any,
  extraArgs: {[key: string]: any}
) => {
  if (!Types.isSimpleType(items.type) && items.type.type === 'array') {
    // TODO: This doesn't quite seem right, it assumes oneOrMany.fromOp was another
    // array.
    return opArray(
      spread(
        Object.values(items.fromOp.inputs).map((v: any) =>
          opFn({[firstArgName]: v, ...extraArgs})
        )
      ) as any
    );
  }
  return opFn({[firstArgName]: items, ...extraArgs});
};

export const applyOpToOneOrManyUnsafe = (
  opFn: any,
  firstArgName: string,
  items: any,
  extraArgs: {[key: string]: any}
) => {
  if (!Types.isSimpleType(items.type) && items.type.type === 'array') {
    // TODO: This doesn't quite seem right, it assumes oneOrMany.fromOp was another
    // array.
    return opArrayUnsafe(
      spread(
        Object.values(items.fromOp.inputs).map((v: any) =>
          opFn({[firstArgName]: v, ...extraArgs})
        )
      ) as any
    );
  }
  return opFn({[firstArgName]: items, ...extraArgs});
};

export function constNodeUnsafe<T extends Types.Type>(type: T, value: any) {
  return {
    nodeType: 'const' as const,
    type,
    val: value,
  };
}

export function constNode<T extends Types.Type>(
  type: T,
  value: Types.TypeToTSTypeInner<T>
) {
  return {
    nodeType: 'const' as const,
    type,
    val: value,
  };
}

export function constRunsFilter(f: any): Graph.ConstNode<'filter'> {
  return {
    nodeType: 'const',
    type: 'filter',
    val: f,
  };
}

export function constString(s: string): Graph.ConstNode<'string'> {
  return {
    nodeType: 'const',
    type: 'string',
    val: s,
  };
}

export function constNumber(n: number): Graph.ConstNode<'number'> {
  return {
    nodeType: 'const',
    type: 'number',
    val: n,
  };
}

export function constBoolean(b: boolean): Graph.ConstNode<'boolean'> {
  return {
    nodeType: 'const',
    type: 'boolean',
    val: b,
  };
}

export function constFunction<T extends Types.Type>(
  fnNode: Graph.OutputNode<T>
): Graph.ConstNode<T> {
  return {
    nodeType: 'const',
    type: fnNode.type,
    val: fnNode as any,
  };
}
